import { Controller } from "@hotwired/stimulus"
import {
  PLAY_ICON_CLASS,
  PLAY_ICON_WRAPPER_CLASS,
  PLAY_TOGGLE_BUTTON_CLASS,
  SOUND_ICON_MUTED_CLASS,
  SOUND_TOGGLE_BUTTON_CLASS,
  applyPlaybackToggleState,
  applySoundToggleState,
  stopPreviewVideo,
  syncInitialPlaybackState,
  togglePlayback,
  toggleSound
} from "../lib/preview_video_playback.js"

export default class extends Controller {
  // mediaContainer: 画像/動画本体だけを差し替える内側コンテナ
  // previewMedia(外側)には矢印・ドット・カウンターも入っているため、そちらは差し替えない
  // soundNote: 音声オン中に「楽曲は含まれません」と出す注記(楽曲が選択されている投稿のみ)
  static targets = ["mediaContainer", "prevButton", "nextButton", "counter", "dots", "soundNote"]
  static values = {
    postType: String,
    audioSelected: Boolean
  }

  connect() {
    this.mediaItems = this.collectMediaItems()
    this.currentIndex = 0
    this.render()
  }

  // Turbo のキャッシュ複製や画面遷移後に音が残らないよう、離脱時に内部の動画を止めてミュートに戻す(フォーム側と揃える)
  disconnect() {
    this.element.querySelectorAll("video").forEach((video) => stopPreviewVideo(video))
  }

  prev(event) {
    event.preventDefault()
    if (this.currentIndex <= 0) return

    this.currentIndex -= 1
    this.render()
  }

  next(event) {
    event.preventDefault()
    if (this.currentIndex >= this.mediaItems.length - 1) return

    this.currentIndex += 1
    this.render()
  }

  goTo(event) {
    event.preventDefault()
    const index = parseInt(event.currentTarget.dataset.index, 10)
    // ドットは全メディア行から生成されるが mediaItems は添付済みのみ。件数がずれても範囲外を描画しない
    if (Number.isNaN(index) || index < 0 || index >= this.mediaItems.length) return

    this.currentIndex = index
    this.render()
  }

  render() {
    if (!this.hasMediaContainerTarget || this.mediaItems.length === 0) return

    const current = this.mediaItems[this.currentIndex]
    // 差し替える前に旧動画の音を止める(音声オンのままスライドを切り替えて音だけ残らないように)
    stopPreviewVideo(this.mediaContainerTarget.querySelector("video"))
    this.mediaContainerTarget.replaceChildren()
    // 作り直した動画は常にミュートなので注記も隠す(画像スライドではボタンごと無い)
    applySoundToggleState(null, this.soundNote, true)

    if (current.type === "video") {
      this.mediaContainerTarget.appendChild(this.buildVideo(current))
    } else {
      const image = document.createElement("img")
      image.src = current.url
      image.className = "w-full max-h-[32rem] object-contain"
      image.alt = "プレビュー"

      this.mediaContainerTarget.appendChild(image)
    }

    this.updateControls()
  }

  // 動画本体と、再生/一時停止を切り替える透明ボタンを重ねたラッパーを作る
  buildVideo(current) {
    const wrapper = document.createElement("div")
    wrapper.className = "relative w-full flex items-center justify-center"

    const video = document.createElement("video")
    video.className = "w-full max-h-[32rem] object-contain"
    video.autoplay = true
    video.muted = true
    // muted プロパティは属性に反映されない。Turbo のキャッシュ複製(cloneNode)でもミュートが残るよう属性でも付ける
    video.setAttribute("muted", "")
    video.loop = true
    video.playsInline = true

    // type 属性は付けずブラウザに判定させる(video/quicktime を type に出すと Chrome が読み込みをスキップする)
    const source = document.createElement("source")
    source.src = current.url
    video.appendChild(source)

    const button = document.createElement("button")
    button.type = "button"
    button.className = PLAY_TOGGLE_BUTTON_CLASS
    button.setAttribute("aria-label", "動画を一時停止")
    button.setAttribute("data-play-toggle", "")

    const iconWrapper = document.createElement("span")
    iconWrapper.className = PLAY_ICON_WRAPPER_CLASS
    iconWrapper.setAttribute("data-play-icon", "")
    const iconCircle = document.createElement("span")
    iconCircle.className = PLAY_ICON_CLASS
    const icon = document.createElement("span")
    icon.className = "iconify lucide--play size-7 text-white"
    iconCircle.appendChild(icon)
    iconWrapper.appendChild(iconCircle)
    button.appendChild(iconWrapper)

    button.addEventListener("click", (event) => {
      event.preventDefault()
      togglePlayback(video)
    })
    video.addEventListener("play", () => applyPlaybackToggleState(button, false))
    video.addEventListener("pause", () => applyPlaybackToggleState(button, true))

    const soundButton = this.buildSoundButton()
    soundButton.addEventListener("click", (event) => {
      event.preventDefault()
      toggleSound(video)
    })
    // ボタン・注記の表示は volumechange で video.muted だけから作る(どの経路でミュートが変わっても表示が追従する)
    video.addEventListener("volumechange", () => {
      applySoundToggleState(soundButton, this.soundNote, video.muted, this.audioSelectedValue)
    })

    wrapper.appendChild(video)
    wrapper.appendChild(button)
    // 再生トグル(inset-0)より後ろに置いて手前にする
    wrapper.appendChild(soundButton)
    syncInitialPlaybackState(video, button)
    applySoundToggleState(soundButton, this.soundNote, video.muted, this.audioSelectedValue)
    return wrapper
  }

  // 右下のスピーカーボタン(元の動画の音声のオン/オフ)。初期状態はミュート
  buildSoundButton() {
    const button = document.createElement("button")
    button.type = "button"
    button.className = SOUND_TOGGLE_BUTTON_CLASS
    button.setAttribute("aria-label", "音声をオンにする")
    button.setAttribute("aria-pressed", "false")
    button.setAttribute("data-sound-toggle", "")

    const icon = document.createElement("span")
    icon.className = `iconify ${SOUND_ICON_MUTED_CLASS} size-5 text-white`
    icon.setAttribute("data-sound-icon", "")
    button.appendChild(icon)
    return button
  }

  get soundNote() {
    return this.hasSoundNoteTarget ? this.soundNoteTarget : null
  }

  collectMediaItems() {
    const items = Array.from(this.element.querySelectorAll("[data-preview-item]"))
    return items.map((item) => ({
      type: item.dataset.previewType,
      url: item.dataset.previewUrl
    }))
  }

  updateControls() {
    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.classList.toggle("opacity-0", this.currentIndex === 0)
      this.prevButtonTarget.classList.toggle("pointer-events-none", this.currentIndex === 0)
    }

    if (this.hasNextButtonTarget) {
      const isLast = this.currentIndex === this.mediaItems.length - 1
      this.nextButtonTarget.classList.toggle("opacity-0", isLast)
      this.nextButtonTarget.classList.toggle("pointer-events-none", isLast)
    }

    if (this.hasCounterTarget) {
      this.counterTarget.textContent = `${this.currentIndex + 1}/${this.mediaItems.length}`
    }

    if (this.hasDotsTarget) {
      this.dotsTarget.querySelectorAll("[data-index]").forEach((dot) => {
        const index = parseInt(dot.dataset.index, 10)
        dot.classList.toggle("bg-blue-500", index === this.currentIndex)
        dot.classList.toggle("bg-white/50", index !== this.currentIndex)
      })
    }
  }
}
